/**
 * Chapter031.js — Site Rules (OHSE 031)
 * Migrated from SWMS Platform Chapter031.tsx → JavaScript
 *
 * Props:
 *   companyName {string}  from companyInfo.name
 */

const DECLARATION_SECTIONS = [
  {
    title: 'SUPERVISOR and SAFETY',
    text: 'All [Company Name] requirements regarding safety whether written or verbal are to be obeyed. The [Company Name] supervisor is in charge of the site. His decision is final and must be followed. All legislative and recommended safety matters such as the Work Health & Safety Act as amended, Best Practice for the Construction Industry, Australian Standards etc. must be followed at all times. Failure to do so, may cause suspension of the person\'s right to work on a [Company Name] site.',
  },
  {
    title: 'Sign In and Out Daily',
    text: 'Each person attending or working at an [Company Name] site is required to have their name and time in and time out entered in the attendance sheet by the [Company Name] supervisor. This assists in an emergency to be able to determine if everyone has safely left the building when an evacuation occurs.',
  },
  {
    title: 'Smoking on [Company Name] Sites',
    text: 'Smoking on [Company Name] sites is prohibited. If required, a special area for smokers will be set aside outside the building.',
  },
  {
    title: 'Alcohol and Drugs',
    text: 'Possession or use of alcohol or recreational drugs is totally prohibited on [Company Name] sites. Any person found using or in possession of alcohol or drugs on a [Company Name] site will be required to leave the site immediately. If employed by [Company Name], the offender may be dismissed or required to undertake disciplinary action. If a contractor, then that person\'s contract will be cancelled immediately. However [Company Name] will assist in any way they can any person who requires help with an addiction.',
  },
  {
    title: 'Qualified Work',
    text: 'All people on a [Company Name] site must be qualified to undertake the work they are going to perform. Licences for some machinery or specific tasks are required both by law and by [Company Name]. No person may operate machinery for which a licence or permit is required without first proving to [Company Name] that they hold the required licence or permit.',
  },
  {
    title: 'Safe Work Method Statements',
    text: 'Before a contractor or his / her employees may start work on a [Company Name] site, the contractor must produce to [Company Name] a suitable completed Safe Work Method Statement for each site. [Company Name] will assist any contractor who requires help to complete a document and can provide a blank generic SWMS if needed.',
  },
  {
    title: 'Dangerous / Hazardous Goods, Chemicals etc.',
    text: 'All goods considered to be potentially dangerous or hazardous must be approved for use by the [Company Name] supervisor on site. A complete Materials Safety Data Sheet must be provided to the [Company Name] supervisor prior to the goods being used on site.',
  },
  {
    title: 'Tidiness',
    text: 'All people working at a [Company Name] site are required to maintain a clean and tidy work area at all times. Materials must be stacked so as not to pose a threat for someone tripping on the items. Food scraps, papers, plastic, drink containers etc. must be removed from the eating area and placed in the bins provided.',
  },
  {
    title: 'Training',
    text: '[Company Name] will provide training to any person whom they consider may not be able to safely undertake any specific task. The person is required to attend the training and accept the need and value of such training.',
  },
  {
    title: 'Emergencies',
    text: 'Emergencies can occur suddenly and without warning. Emergencies may take any of a number of forms. Some of these forms could be: fire, explosion, uncontained chemical spill, lack of ventilation, surprise attack, terrorist attack, building collapse or one of many others. In the event of an emergency on a [Company Name] site, all people are required to immediately vacate the premises according to the emergency evacuation plan provided to them and to assemble at the nominated assembly point.',
  },
  {
    title: 'Discrimination',
    text: 'Discrimination of any nature will not be tolerated. This includes but is not limited to discrimination because of: Race, Religion, Skin Colour, Sex or Sexual Preferences, Handicaps, or for any other reason. All complaints of discrimination will be immediately handed to the authorities for investigation and possible prosecution.',
  },
  {
    title: 'Personal Protective Equipment',
    text: 'All persons working on a [Company Name] site are required to wear steel capped safety boots / shoes, high visibility vests or shirt and hard hats at all times while working. Additional PPE such as eye and hearing protection, sun protection, gloves, knee protection, wide brimmed hat etc. must be worn by every person as appropriate.',
  },
  {
    title: 'Equipment Safety',
    text: 'All equipment used on site must comply with the manufacturer\'s directions in regard to use and maintenance. Where required (such as for electrical equipment) a record of maintenance, testing and tagging dates, etc. must be maintained on site and available for inspection at any time. Guards provided for machinery must always be attached and used. All electrical equipment must be attached through a residual current device (RCD). Double adaptors, piggy-back leads, power boards etc. are prohibited on site.',
  },
  {
    title: 'General',
    text: '[Company Name] wishes to see that every person working on their sites go home in good condition, without injury or ill health. This can only be done by everyone being aware of Health & Safety on site and being aware of and obeying the rules for safety of personnel and the environment. We take safety seriously and expect that you will too.',
  },
];

/** Replace [Company Name] with actual company name rendered as gold spans */
function renderText(text, cn) {
  const parts = text.split('[Company Name]');
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <span className="font-semibold text-[#c9a84c]">{cn}</span>
      )}
    </span>
  ));
}

export default function Chapter031({ companyName }) {
  const cn = companyName || '[Company Name]';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 031</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Site Rules</h2>
      </div>

      {/* Site rules card */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Site Rules</h3>
          <p className="text-xs text-gray-500 mt-0.5">Company site rules and safety requirements for all personnel</p>
        </div>
        <div className="p-6 space-y-5">
          {DECLARATION_SECTIONS.map((section, idx) => (
            <div key={idx} className="border-l-2 border-[#c9a84c]/40 pl-4">
              <h4 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide mb-1.5">
                {renderText(section.title, cn)}
              </h4>
              <p className="text-sm text-[#1a1a2e] leading-relaxed">
                {renderText(section.text, cn)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
